import React, { Component } from 'react';
import DockPanel from './DockPanel/DockPanel';
import {Form, Modal, Button, Spinner, Table, Tabs, Tab} from "react-bootstrap";
import EnsResolver from "ens-resolver";
import MarkdownPreview from '@uiw/react-markdown-preview';
import ExternalMintPanel from './ExternalMintPanel';
import { externalMintFor } from '../config/externalMints';

class MintModal extends Component {

  constructor(props) {
    super(props);
    this.state = { licenseAccepted: false, gateAcknowledged: false };
  }

  componentDidUpdate(prevProps) {
    if (this.props.isMintOpen && !prevProps.isMintOpen) {
      this.setState({ licenseAccepted: false, gateAcknowledged: false });
    }
  }

  render() {
    const { account, projectMintLoading, isMintOpen, projectIsSoldOut,
      closeMintModal, onSubmitMint,
      userIsAdmin,
      selectedProjectInformation, projects, projectId, platform, contract,
      projectPricePerEditionInEth, projectArtistAddress, artistPercent,
      projectAdditionalPayeePercent, projectAdditionalPayee,
      getAbbreviatedHash, pagePosts, warnScriptPosterArtistMismatch,
      scriptPoster, projectRoyaltyAddress } = this.props;

    // Find the info post (filter out hidden posts, then find last markdown match)
    const posts = pagePosts || [];
    const hiddenTxHashes = new Set(posts.filter(p => p.tag && p.tag.startsWith('H')).map(p => p.tag.slice(1)));
    const visiblePosts = posts.filter(p => !hiddenTxHashes.has(p.transactionHash));
    const isInfoMarkdown = (content) => {
      if (!content || !/^.{0,10}#/m.test(content)) return false;
      const textOutsideFences = content.replace(/```[\s\S]*?```/g, '').trim();
      return textOutsideFences.length > 20;
    };
    // The tag is authoritative. Content-sniffing alone misreads a stylesheet
    // as the description, because CSS hex colours (#0d0d0d) look like markdown
    // headers to the check -- so a style post showed up as info text.
    const NON_INFO_TAGS = ['style.immutables.art', 'style', 'code'];
    const infoPosts = visiblePosts.filter(p =>
      NON_INFO_TAGS.indexOf(p.tag) < 0 && isInfoMarkdown(p.content));
    const infoPost = infoPosts.length > 0 ? infoPosts[infoPosts.length - 1] : null;

    const project = projects[projectId - 1];

    // Paused deliberately, to route minting through a gating contract: either
    // one we have an entry for, or one betrayed by the script's poster
    // differing from the project's artist address.
    const isGated = !!(project && project.paused &&
      (externalMintFor(projectId) || warnScriptPosterArtistMismatch));

    // The pause exempts the artist and the team, so these accounts can mint a
    // gated project directly, bypassing whatever the gate would have asked.
    const canBypassPause = (account === projectArtistAddress || userIsAdmin);

    // The ordinary mint, lifted out so it can appear either as the main tab or,
    // on a gated project, tucked into a tab of its own. Bypassing a gate asks
    // for a second, separate acknowledgement: the licence checkbox is about
    // the edition, this one is about overriding the artist's own condition.
    const renderMintForm = (requireGateAck) => (
      <>
        {account && (<>
        <p></p>
        <span className="font-display modal-account-text">
          <b>minting with account: </b>
        </span>
        <br/>
        <span className="font-display modal-account-text">
          <EnsResolver lookup={account}/>
          &nbsp;
          ({getAbbreviatedHash(account, 4, 4)})
        </span>
        </>)}
        <p></p>
        <p></p>
        <p></p>

        <Button
          variant={this.state.licenseAccepted ? "outline-secondary" : "outline-primary"}
          size="sm"
          className="font-display"
          style={{marginBottom: '1em', marginTop: '1em'}}
          onClick={() => this.setState({ licenseAccepted: !this.state.licenseAccepted })}
        >
          {this.state.licenseAccepted ? '✓ ' : ''}{infoPost ? (
            <span>I agree to the Edition License<br/>specified in the <b>Project Information</b> tab</span>
          ) : (
            <span>I acknowledge that no project-specific Edition License<br/>has been specified</span>
          )}
        </Button>
        <br/>
        {requireGateAck && (
          <>
          <Button
            variant={this.state.gateAcknowledged ? "outline-secondary" : "outline-danger"}
            size="sm"
            className="font-display"
            style={{marginBottom: '1em'}}
            onClick={() => this.setState({ gateAcknowledged: !this.state.gateAcknowledged })}
          >
            {this.state.gateAcknowledged ? '✓ ' : ''}
            <span>I understand this project is meant to be minted<br/>through its gating contract, and this bypasses it</span>
          </Button>
          <br/>
          </>
        )}
        <Form onSubmit={onSubmitMint}>
          <Button
            id="submit"
            type="submit"
            variant="success btn-lg"
            className="font-display"
            disabled={!this.state.licenseAccepted || (requireGateAck && !this.state.gateAcknowledged)}>
            {`mint (${projectPricePerEditionInEth}Ξ)`}
          </Button>
        </Form>
      </>
    );

    const panelFooter = (
      <>
                {/*
                <Button variant="warning btn-sm" className="font-display" href="#" onClick={() => window.open(`${mintingTermsAndConditionsURL}`)}>
                  minting terms and conditions
                </Button>
                */}
                {(contract && contract.options) ? (
                  <>
                  <Button variant="outline-secondary btn-sm" className="font-display" onClick={() => window.open(`${platform.etherscan_link}${contract.options.address}`)}>etherscan</Button>
                  </>
                ) : ("")}
                <Button variant="secondary btn-sm" className="font-display" onClick={closeMintModal}>
                  close
                </Button>
      </>
    );

    return (
      <DockPanel
        show={isMintOpen}
        onClose={closeMintModal}
        title={<>Mint {(projects[projectId-1]) ? projects[projectId-1].name : ""}</>}
        storageKey="mint"
        defaultWidth={640}
        defaultHeight={560}
        minWidth={360}
        minHeight={240}
        footer={panelFooter}
      >
        <div className="dock-panel__pad">

              <Tabs defaultActiveKey="mint_details" id="mint-modal-tabs">
              <Tab eventKey="mint_details" title="Mint">

              <div className="col-lg-12 d-flex text-center">
                <div className="content mr-auto ml-auto">

                {/*
                  The header a person came for: what this is, whose it is, what
                  it says, how many exist. The addresses and the fee split are
                  real but they are reference material, and they were sitting
                  on top of the mint.
                */}
                {project && (
                  <div className="mint-head">
                    <h4 className="font-display mint-head__title">{project.name}</h4>
                    {project.artist && (
                      <div className="font-display mint-head__by">by {project.artist}</div>
                    )}
                    {project.description && (
                      <p className="mint-head__desc">{project.description}</p>
                    )}

                    <div className="mint-head__meta font-display">
                      <span className="mint-head__count">
                        <b>{project.currentEditionId}</b>
                        {project.maxEditions ? <> of {project.maxEditions}</> : null} minted
                      </span>
                      {projectPricePerEditionInEth !== undefined && !isGated && (
                        <span className="mint-head__price">{projectPricePerEditionInEth}Ξ</span>
                      )}
                    </div>

                    <div className="mint-head__links font-display">
                      {projectArtistAddress && (
                        <a href={`${platform.etherscan_link}${projectArtistAddress}`} target="_new" rel="noopener noreferrer">artist</a>
                      )}
                      {scriptPoster && (
                        <a href={`${platform.etherscan_link}${scriptPoster}`} target="_new" rel="noopener noreferrer">script author</a>
                      )}
                      {projectRoyaltyAddress && (
                        <a href={`${platform.etherscan_link}${projectRoyaltyAddress}`} target="_new" rel="noopener noreferrer">royalty manager</a>
                      )}
                      {(contract && contract.options) && (
                        <a href={`${platform.etherscan_link}${contract.options.address}`} target="_new" rel="noopener noreferrer">contract</a>
                      )}
                    </div>
                  </div>
                )}

                { (projectMintLoading) ? (
                  <>
                  <p>Please wait while your Edition is minted ...</p>
                  <div className="text-center"><Spinner animation="grow" /></div>
                  </>
                ) : (
                  (projects[projectId-1]) ? (
                    (!projectIsSoldOut) ? (
                      isGated ? (
                        // A gated project shows everyone the same thing here:
                        // where the public actually mints it. An admin or the
                        // artist can still mint directly, but that belongs in
                        // its own tab rather than in front of the answer most
                        // visitors came for.
                        <ExternalMintPanel
                          projectId={projectId}
                          account={account}
                          contract={contract}
                          gatedUnknown={warnScriptPosterArtistMismatch}
                          onNavigate={closeMintModal}
                        />
                      ) :
                      (!projects[projectId-1].paused || canBypassPause) ?
                        renderMintForm(false) :
                        (   // Paused, and not for any reason we can explain
                          <Form onSubmit={onSubmitMint}>
                          <Button
                            id="submit"
                            type="submit"
                            variant="secondary btn-lg"
                            className="font-display"
                            disabled>
                            {`minting currently paused`}
                            </Button>
                          </Form>
                        )
                    ) : (   // Sold Out
                      <Form onSubmit={onSubmitMint}>
                      <Button
                        id="submit"
                        type="submit"
                        variant="danger btn-lg"
                        className="font-display"
                        disabled>
                        {`sold out`}
                        </Button>
                      </Form>
                    )
                  ) : ("")
                )}

                </div>
              </div>

              </Tab>

              {/*
                The direct mint an artist or teammember keeps on a gated
                project. Its own tab, because bypassing the gate is not what
                this modal is for -- it is the exception, and the exception
                should not sit where the answer goes.
              */}
              {(isGated && canBypassPause && !projectIsSoldOut) && (
              <Tab eventKey="mint_privileged"
                   title={account === projectArtistAddress ? 'Artist mint' : 'Team mint'}>
                <div className="col-lg-12 d-flex text-center">
                  <div className="content mr-auto ml-auto">
                    <p></p>
                    <p className="font-display" style={{marginTop: '1em'}}>
                      <b>Minting directly, past the gate</b>
                    </p>
                    <p className="modal-fine-print">
                      This project is paused to the public so that minting runs through
                      its gating contract. The pause does not apply to you, so this mints
                      an edition straight from the platform contract without the gate's
                      conditions being checked.
                    </p>
                    {renderMintForm(true)}
                  </div>
                </div>
              </Tab>
              )}

              {/*
                Everything true but not urgent: where the money goes, and every
                address involved. It belongs one click away, not above the mint.
              */}
              <Tab eventKey="mint_details_tab" title="Details">
                <div className="col-lg-12 d-flex text-center">
                  <div className="content mr-auto ml-auto">
                    <p></p>
                    <p className="font-display"><b>Fee Split</b></p>
                    <p className="modal-fine-print">100% of the mint fee is sent to the project's Royalty Manager contract, which is configured to disperse funds according to the table below.</p>
                    <Table size="sm" className="modal-fine-print" style={{textAlign: 'left'}}>
                      <thead>
                        <tr>
                          <th>%</th>
                          <th>Recipient</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{(artistPercent - projectAdditionalPayeePercent)/100}%</td>
                          <td>Artist: <a href={`${platform.etherscan_link}${projectArtistAddress}`} target="_new"><EnsResolver lookup={projectArtistAddress}/> ({getAbbreviatedHash(projectArtistAddress, 4, 4)})</a></td>
                        </tr>
                        {(projectAdditionalPayeePercent > 0) ? (
                        <tr>
                          <td>{projectAdditionalPayeePercent/100}%</td>
                          <td>Additional payee (from artist's share): <a href={`${platform.etherscan_link}${projectAdditionalPayee}`} target="_new"><EnsResolver lookup={projectAdditionalPayee}/></a></td>
                        </tr>
                        ) : ""}
                        <tr>
                          <td>{100-artistPercent/100}%</td>
                          <td>Deposited in the {(contract && contract.options) ? (<a href={`${platform.etherscan_link}${contract.options.address}`} target="_new">Immutables.art contract</a>) : ("Immutables.art contract")}</td>
                        </tr>
                      </tbody>
                    </Table>

                    <p></p>
                    <div className="text-center">{selectedProjectInformation}</div>
                  </div>
                </div>
              </Tab>

              {infoPost && (
              <Tab eventKey="mint_project_info" title="Project Information">
                <p></p>
                <div className="info-post-content">
                  <MarkdownPreview source={infoPost.content} />
                </div>
                <p></p>
              </Tab>
              )}

              </Tabs>
        </div>
      </DockPanel>
    );
  }
}

export default MintModal;
